import type { DataField } from '@toolpad/core';
import type { UserTableColumnRole, UserTableColumnsByRole } from 'types';
import type {
  GridSortModel,
  GridFilterModel,
  GridInitialState,
} from '@mui/x-data-grid-pro';

// Role key order is irrelevant; column array order is part of the policy.
export function getUserTableColumnsSignature(
  config: UserTableColumnsByRole | null | undefined
): string {
  return JSON.stringify(
    Object.entries(config ?? {})
      .filter(([, fields]) => Array.isArray(fields) && fields.length > 0)
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([role, fields]) => [
        role,
        [...new Set(fields.filter((field) => typeof field === 'string'))],
      ])
  );
}

export function applyUserTableColumns(
  fields: DataField[],
  role: string | undefined,
  config: UserTableColumnsByRole | null = {}
): DataField[] {
  const allowed = config?.[role as UserTableColumnRole];
  if (!Array.isArray(allowed) || !allowed.length) return fields;
  const fieldsByName = new Map(fields.map((field) => [field.field, field]));
  return [
    ...[...new Set(allowed)].flatMap((name) => {
      const field = fieldsByName.get(name);
      return field && field.field !== 'actions' ? [field] : [];
    }),
    ...fields.filter((field) => field.field === 'actions'),
  ];
}

function isRecord(value: unknown): boolean {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function sanitizeFieldList(
  value: unknown,
  fields: readonly string[]
): string[] {
  return Array.isArray(value)
    ? value.filter(
        (field) => typeof field === 'string' && fields.includes(field)
      )
    : [];
}

// Quick search remains global, matching the existing API search behavior.
export function sanitizeGridFilter(
  model: GridFilterModel,
  fields: readonly string[]
): GridFilterModel {
  if (!isRecord(model)) return { items: [] };
  return {
    ...model,
    ...(model.quickFilterValues !== undefined && {
      quickFilterValues: Array.isArray(model.quickFilterValues)
        ? model.quickFilterValues.filter((value) =>
            ['string', 'number', 'boolean'].includes(typeof value)
          )
        : [],
    }),
    items: Array.isArray(model.items)
      ? model.items.filter((item) => item && fields.includes(item.field))
      : [],
  };
}

export function sanitizeGridSort(
  model: GridSortModel,
  fields: readonly string[]
): GridSortModel {
  return Array.isArray(model)
    ? model.filter(
        (item) =>
          item &&
          fields.includes(item.field) &&
          (item.sort === 'asc' || item.sort === 'desc')
      )
    : [];
}

export function sanitizeGridState(
  state: GridInitialState,
  fields: readonly string[]
): GridInitialState {
  if (!isRecord(state)) return {};
  return {
    ...state,
    ...(state.columns && {
      columns: {
        ...state.columns,
        ...(state.columns.orderedFields && {
          orderedFields: sanitizeFieldList(state.columns.orderedFields, fields),
        }),
        ...(state.columns.columnVisibilityModel && {
          columnVisibilityModel: Object.fromEntries(
            Object.entries(state.columns.columnVisibilityModel).filter(
              ([field]) => fields.includes(field)
            )
          ),
        }),
        ...(state.columns.dimensions && {
          dimensions: Object.fromEntries(
            Object.entries(state.columns.dimensions).filter(([field]) =>
              fields.includes(field)
            )
          ),
        }),
      },
    }),
    ...(state.pinnedColumns && {
      pinnedColumns: {
        ...(state.pinnedColumns.left !== undefined && {
          left: sanitizeFieldList(state.pinnedColumns.left, fields),
        }),
        ...(state.pinnedColumns.right !== undefined && {
          right: sanitizeFieldList(state.pinnedColumns.right, fields),
        }),
      },
    }),
    ...(state.filter?.filterModel && {
      filter: {
        ...state.filter,
        filterModel: sanitizeGridFilter(state.filter.filterModel, fields),
      },
    }),
    ...(state.sorting?.sortModel && {
      sorting: {
        ...state.sorting,
        sortModel: sanitizeGridSort(state.sorting.sortModel, fields),
      },
    }),
  };
}

export function sanitizeGridSearch(
  search: string,
  fields: readonly string[],
  resetPage = false
): string {
  const params = new URLSearchParams(search);
  for (const key of ['filter', 'sort'] as const) {
    const raw = params.get(key);
    if (!raw) continue;
    try {
      const model = JSON.parse(raw);
      if (key === 'filter' ? !isRecord(model) : !Array.isArray(model)) {
        params.delete(key);
        continue;
      }
      const sanitized =
        key === 'filter'
          ? sanitizeGridFilter(model, fields)
          : sanitizeGridSort(model, fields);
      if (JSON.stringify(model) !== JSON.stringify(sanitized))
        params.set(key, JSON.stringify(sanitized));
    } catch {
      params.delete(key);
    }
  }
  if (resetPage) params.delete('page');
  return params.toString();
}
